import { Link, useParams } from 'react-router-dom';
import { routes } from '../routes/routes';
import useApi from '../api';
import { useQuery } from 'react-query';
import queryConfig from '../util/queryConfig';
import Throbber from '../components/throbber/Throbber';
import Error from '../components/error/Error';
import { QueryKey } from '../constants/queryKey';

const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '—';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const StatusBadge = ({ status }) => {
    const colors = {
        completed: { bg: '#e6f4ea', color: '#1e7e34' },
        failed:    { bg: '#f4e6e6', color: '#c0392b' },
        pending:   { bg: '#fff3cd', color: '#856404' },
        preparing: { bg: '#d1ecf1', color: '#0c5460' },
        rendering: { bg: '#d1ecf1', color: '#0c5460' },
    };
    const c = colors[status] || colors.pending;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: '3px',
            fontSize: '13px', fontWeight: 600, background: c.bg, color: c.color,
        }}>
            {status}
        </span>
    );
};

const Section = ({ title, children }) => (
    <div style={{ marginBottom: '24px' }}>
        <h3 className="heading heading--sm" style={{ marginBottom: '8px', borderBottom: '1px solid #e0e0e0', paddingBottom: '4px' }}>{title}</h3>
        {children}
    </div>
);

const Row = ({ label, value, href }) => (
    <div className="table__row">
        <div className="table__label">{label}</div>
        <div className="table__value">
            {href ? <a href={href} target="_blank" rel="noopener noreferrer">{value}</a>
                   : (value || <span style={{ color: '#999' }}>—</span>)}
        </div>
    </div>
);

const VideoDetailView = () => {
    const { requestId } = useParams();
    const { getVideoDetail } = useApi();
    const { isLoading, error, data } = useQuery(
        [QueryKey.VideoDetail, requestId],
        async () => {
            const response = await getVideoDetail(requestId);
            return response.data;
        },
        queryConfig
    );

    const isDev     = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const playerHost = isDev ? `${window.location.protocol}//${window.location.hostname}:3000/test/videogram-player.html` : `${window.location.protocol}//player.${window.location.hostname.replace('app.', '')}/preview.html`;
    const playerUrl = data?.status === 'completed' && data?.scenario_id
        ? `${playerHost}?theVideoGramId=${data.tenant_id}/${data.scenario_id}/${data.request_id}`
        : null;

    return (
        <div className="view video-detail">
            <Link to={routes.history} className="video-detail__back">&larr; Back to history</Link>

            {isLoading ? (
                <Throbber title="Loading video" />
            ) : error ? (
                <Error msg="Could not load video details." />
            ) : (
                <>
                    <h1 className="heading">{data.request_id}</h1>

                    {playerUrl && (
                        <div className="video-detail__player">
                            <div style={{ position: 'relative', width: '100%', maxWidth: '960px', aspectRatio: '16 / 9' }}>
                                <iframe
                                    src={playerUrl}
                                    title="Video preview"
                                    scrolling="no"
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: '4px', background: '#000', overflow: 'hidden' }}
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    )}

                    {data.status === 'failed' && data.error && (
                        <div className="video-detail__error">
                            <strong>Error:</strong> {data.error}
                        </div>
                    )}

                    <div className="table">
                        <Section title="Job Info">
                            <Row label="Status" value={<StatusBadge status={data.status} />} />
                            <Row label="Scenario" value={data.scenario_id} />
                            <Row label="Recipient" value={data.video_recipient} />
                            <Row label="Duration" value={formatDuration(data.video_duration)} />
                            <Row label="Views" value={data.watch_count} />
                            <Row label="Created" value={data.created_at} />
                            <Row label="Completed" value={data.completed_at} />
                        </Section>

                        {data.video_url && (
                            <Section title="Links">
                                <Row label="Video" value={data.video_url} href={data.video_url} />
                                <Row label="Data JSON" value={data.data_url} href={data.data_url} />
                            </Section>
                        )}

                        {data.data && Object.keys(data.data).length > 0 && (
                            <Section title="Render Input">
                                {Object.entries(data.data).map(([key, val]) => (
                                    <Row key={key} label={key} value={
                                        typeof val === 'object' ? JSON.stringify(val) : String(val)
                                    } />
                                ))}
                            </Section>
                        )}
                    </div>

                    <Link to={routes.history} className="button" style={{ marginTop: '20px', display: 'inline-block' }}>
                        Back to history
                    </Link>
                </>
            )}
        </div>
    );
};

export default VideoDetailView;
